import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { useAdminUser } from './AdminContext';
import { ReauthDialog } from './ui/ReauthDialog';
import { ApiError } from '../core/api/http';

/** Uitkomst van een bewaakte actie: gedaan (met antwoord), of bevestigen geannuleerd. */
export type Guarded<T> = {
  done: true;
  value: T;
} | {
  done: false;
};

/**
 * Gevoelige acties (rollen, stoppen, sessies, klantexport) vragen een recente bevestiging. Weigert
 * de server met 423 `reauth_required`, dan vraagt dit om wachtwoord (en code) en probeert het één
 * keer opnieuw. Annuleren is geen fout: `{ done: false }`. Andere fouten gaan gewoon door.
 */
export function useReauth(reason: string): {
  guard: <T>(fn: () => Promise<T>) => Promise<Guarded<T>>;
  dialog: ReactElement;
} {
  const {
    twoFactor
  } = useAdminUser();
  const resolver = useRef<((ok: boolean) => void) | null>(null);
  const [open, setOpen] = useState(false);
  const finish = useCallback((ok: boolean) => {
    resolver.current?.(ok);
    resolver.current = null;
    setOpen(false);
  }, []);

  // Weg van het scherm terwijl de dialog openstaat: de wachtende actie stopt.
  useEffect(() => () => resolver.current?.(false), []);
  const guard = useCallback(async <T,>(fn: () => Promise<T>): Promise<Guarded<T>> => {
    try {
      return {
        done: true,
        value: await fn()
      };
    } catch (err) {
      if (!(err instanceof ApiError && err.code === 'reauth_required')) throw err;
      // Al een bevestiging open (twee knoppen snel na elkaar): de vorige telt als geannuleerd.
      resolver.current?.(false);
      const ok = await new Promise<boolean>(resolve => {
        resolver.current = resolve;
        setOpen(true);
      });
      if (!ok) return {
        done: false
      };
      return {
        done: true,
        value: await fn()
      };
    }
  }, []);
  const dialog = <ReauthDialog open={open} twoFactor={twoFactor.enabled} reason={reason} onDone={() => finish(true)} onCancel={() => finish(false)} data-id="element-5526" data-mp="hungk576dkb1" />;
  return {
    guard,
    dialog
  };
}